import type { JSX } from 'react';
import type {
  ActivityResource,
  ContactResource,
  DealDetailResource,
  DealDocumentResource,
  DiscoveryReportBody,
  MoveDealStageBody,
  OpenStageValue,
  ReminderResource,
  UpdateDealBody,
} from '@majuscule/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Check, Copy, ExternalLink, FileText, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ActivityComposer, ActivityFeed } from '../components/crm/Activity';
import { ContactDialog, type ContactDialogMode } from '../components/crm/ContactDialog';
import { ConvertDialog } from '../components/crm/ConvertDialog';
import { EditDealDialog } from '../components/crm/DealDialog';
import { DealTitle } from '../components/crm/DealTitle';
import { LostDialog } from '../components/crm/LostDialog';
import { DiscoveryDialog, DocumentDialog } from '../components/crm/MiscDialogs';
import { ReminderDialog, type ReminderDialogMode } from '../components/crm/ReminderDialog';
import {
  backLinkClass,
  DetailHead,
  MiniCell,
  MiniList,
  MiniRow,
  useCrmRefresh,
} from '../components/crm/shared';
import { useToast } from '../components/toast';
import { PageChrome } from '../components/page-chrome';
import { internalFetch } from '../lib/api';
import {
  asOpenStage,
  crmKeys,
  crmSend,
  crmSendEmpty,
  daysInStageLabel,
  DISCOVERY_FIELDS,
  discoveryText,
  errorMessage,
  eurOrDash,
  formatDate,
  formatInstantDate,
  formatLongDate,
  formatReminderDue,
  formatReminderLong,
  LOST_REASON_LABEL,
  MODEL_LABEL,
  nextOpenStage,
  OPEN_STAGES,
  ORIGIN_LABEL,
  parisDateOf,
  percentLabel,
  REMINDER_TYPE_LABEL,
  ROLE_LABEL,
  STAGE_LABEL,
  STAGE_NEXT_SUBJECT,
  stageBadgeVariant,
  DOCUMENT_CATEGORY_LABEL,
} from '../lib/crm';
import { Callout } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardBar, CardContent, CardHead } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState, LoadingState } from '@/components/ui/empty';
import { KvList, KvRow } from '@/components/ui/kv';
import { AppointmentsCard } from '../components/crm/Appointments';
import { ProposalsCard, SignatureCard } from '../components/crm/DealFinance';
import { openDealDocument } from '../components/crm/documents';
import { DealOwner } from '../components/team/OwnerSelect';
import { useCan } from '../lib/me';
import { Split21, Stack } from '@/components/ui/layout-grid';
import { Tag } from '@/components/ui/tag';
import { cn } from '@/lib/utils';

type DiscoveryReport = DiscoveryReportBody;

/** Pinned R1 report of the deal, else its latest one. */
function discoveryOf(
  activities: readonly ActivityResource[],
): { activity: ActivityResource; report: DiscoveryReport } | null {
  const reports = activities.flatMap((activity) =>
    activity.content.kind === 'discovery' ? [{ activity, report: activity.content.report }] : [],
  );
  return reports.find((entry) => entry.activity.pinned) ?? reports[0] ?? null;
}

function Stepper({
  detail,
  onMove,
  busy,
}: {
  detail: DealDetailResource;
  onMove: (stage: OpenStageValue) => void;
  busy: boolean;
}): JSX.Element {
  const { deal } = detail;
  const current = asOpenStage(deal.stage);
  const currentIndex =
    deal.stage === 'won'
      ? OPEN_STAGES.length
      : current !== null
        ? OPEN_STAGES.indexOf(current)
        : -1;
  const reached = (stage: OpenStageValue): string | null => {
    const entries = detail.stageHistory.filter((entry) => entry.to === stage);
    const last = entries[entries.length - 1];
    return last === undefined
      ? null
      : formatDate(parisDateOf(new Date(last.changedAt))).slice(0, 5);
  };
  return (
    <ol
      aria-label="Progression de l’affaire"
      className="m-0 grid list-none grid-cols-3 gap-x-1 gap-y-3 p-0 min-[900px]:grid-cols-6"
    >
      {OPEN_STAGES.map((stage, index) => {
        const skip = deal.model === 'shared_license' && stage === 'specifications';
        const date = reached(stage);
        const isCurrent = index === currentIndex;
        const done = !skip && (deal.stage === 'lost' ? date !== null : index < currentIndex);
        const actionable = current !== null && !isCurrent && !skip;
        const label = (
          <>
            <span>{STAGE_LABEL[stage]}</span>
            <span className="font-mono text-[11px] font-normal leading-4 text-muted-foreground">
              {skip ? 'Sans objet en licence' : (date ?? (done ? '—' : 'À venir'))}
            </span>
          </>
        );
        const className = cn(
          'grid w-full gap-0.5 border-t-[3px] border-border pt-2 text-left text-xs leading-4 text-muted-foreground',
          done && 'border-muted-foreground',
          isCurrent && 'border-foreground font-semibold text-foreground',
          skip && 'border-dashed',
        );
        return (
          <li key={stage} aria-current={isCurrent ? 'step' : undefined}>
            {actionable ? (
              <button
                type="button"
                disabled={busy}
                title={`Passer en ${STAGE_LABEL[stage]}`}
                className={cn(
                  className,
                  'cursor-pointer hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-wait',
                )}
                onClick={() => {
                  onMove(stage);
                }}
              >
                {label}
              </button>
            ) : (
              <div className={className}>{label}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function contactLine(contact: ContactResource): string {
  return `${contact.firstName} ${contact.lastName}`;
}

export function DealDetailPage({ dealId }: { dealId: string }): JSX.Element {
  const refresh = useCrmRefresh();
  const { notify } = useToast();
  const detailQuery = useQuery({
    queryKey: crmKeys.deal(dealId),
    queryFn: () => internalFetch<DealDetailResource>(`/internal/crm/deals/${dealId}`),
  });
  const canConvert = useCan('convert_deal');
  const [lostOpen, setLostOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState<DealDocumentResource | null>(null);
  const [reminder, setReminder] = useState<ReminderDialogMode | null>(null);
  const [contactMode, setContactMode] = useState<ContactDialogMode | null>(null);

  const move = useMutation({
    mutationFn: (stage: OpenStageValue) => {
      const body: MoveDealStageBody = { stage };
      return crmSend<DealDetailResource>('POST', `/deals/${dealId}/stage`, body);
    },
    onSuccess: (updated) => {
      refresh();
      notify(`Affaire passée en ${STAGE_LABEL[updated.deal.stage]}`);
    },
  });
  const done = useMutation({
    mutationFn: (id: string) => crmSend<ReminderResource>('POST', `/reminders/${id}/done`),
    onSuccess: () => {
      refresh();
    },
  });
  const deleteDoc = useMutation({
    mutationFn: (id: string) => crmSendEmpty('DELETE', `/documents/${id}`),
    onSuccess: () => {
      setDeletingDoc(null);
      refresh();
    },
  });
  const linkContact = useMutation({
    mutationFn: (contactIds: string[]) => {
      const body: UpdateDealBody = { contactIds };
      return crmSend<DealDetailResource>('PATCH', `/deals/${dealId}`, body);
    },
    onSuccess: () => {
      refresh();
    },
  });

  const detail = detailQuery.data;
  if (detail === undefined) {
    return (
      <>
        <PageChrome crumbs={[{ label: 'Pipeline', to: '/pipeline' }, { label: 'Affaire' }]} />
        {detailQuery.isError ? (
          <Callout variant="destructive">Impossible de charger l’affaire.</Callout>
        ) : (
          <LoadingState />
        )}
      </>
    );
  }

  const { deal, company } = detail;
  const open = asOpenStage(deal.stage) !== null;
  const next = nextOpenStage(deal.model, deal.stage);
  const discovery = discoveryOf(detail.activities);
  const nextReminder =
    deal.nextAction !== null
      ? (detail.reminders.find((item) => item.id === deal.nextAction?.reminderId) ?? null)
      : null;
  const otherReminders = detail.reminders.filter(
    (item) => item.doneAt === null && item.id !== nextReminder?.id,
  );
  const mutationError = move.isError
    ? errorMessage(move.error, 'Échec du changement d’étape.')
    : done.isError
      ? errorMessage(done.error, 'Échec de la mise à jour du rappel.')
      : linkContact.isError
        ? errorMessage(linkContact.error, 'Échec du rattachement du contact.')
        : null;

  return (
    <>
      <PageChrome
        crumbs={[{ label: 'Pipeline', to: '/pipeline' }, { label: deal.title }]}
        actions={
          open ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setLostOpen(true);
                }}
              >
                Marquer comme perdue
              </Button>
              {next !== null ? (
                <>
                  {canConvert ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setConvertOpen(true);
                      }}
                    >
                      Marquer comme gagnée
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    disabled={move.isPending}
                    onClick={() => {
                      move.mutate(next);
                    }}
                  >
                    <ArrowRight aria-hidden="true" />
                    Passer en {STAGE_LABEL[next]}
                  </Button>
                </>
              ) : canConvert ? (
                <Button
                  type="button"
                  onClick={() => {
                    setConvertOpen(true);
                  }}
                >
                  <Check aria-hidden="true" />
                  Passer en client
                </Button>
              ) : (
                <span className="text-caption text-muted-foreground">
                  Passage en client : par un administrateur
                </span>
              )}
            </>
          ) : undefined
        }
      />
      <DetailHead
        back={
          <Link
            to="/companies/$companyId"
            params={{ companyId: company.id }}
            className={backLinkClass}
          >
            <ArrowLeft aria-hidden="true" />
            {company.name}
          </Link>
        }
        title={deal.title}
        heading={<DealTitle deal={deal} editable={open} />}
        badges={
          <>
            <Badge variant={stageBadgeVariant(deal.stage)}>{STAGE_LABEL[deal.stage]}</Badge>
            <Tag>{MODEL_LABEL[deal.model]}</Tag>
            <span className="text-[13px] leading-[18px] text-muted-foreground">
              Créée le {formatLongDate(parisDateOf(new Date(deal.createdAt)))}
              {open ? `, ${daysInStageLabel(deal.daysInStage).toLowerCase()}` : ''}
            </span>
          </>
        }
      />

      {deal.stage === 'won' ? (
        <Callout variant="success">
          Affaire gagnée{deal.closedOn !== null ? ` le ${formatLongDate(deal.closedOn)}` : ''}.
        </Callout>
      ) : null}
      {deal.stage === 'lost' ? (
        <Callout variant="default">
          Affaire perdue{deal.closedOn !== null ? ` le ${formatLongDate(deal.closedOn)}` : ''}
          {deal.lostReason !== null ? `, motif : ${LOST_REASON_LABEL[deal.lostReason]}` : ''}.
        </Callout>
      ) : null}
      {mutationError !== null ? <Callout variant="destructive">{mutationError}</Callout> : null}

      <Card>
        <CardContent>
          <Stepper
            detail={detail}
            busy={move.isPending}
            onMove={(stage) => {
              move.mutate(stage);
            }}
          />
        </CardContent>
      </Card>

      <Split21 className="items-start">
        <Stack>
          <Card>
            <CardHead
              title="Compte rendu de R1"
              description={
                discovery !== null
                  ? `Rendez-vous découverte du ${formatLongDate(parisDateOf(new Date(discovery.activity.occurredAt)))}${discovery.activity.pinned ? ', épinglé' : ''}`
                  : 'Rendez-vous découverte'
              }
              actions={
                <>
                  {discovery !== null ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void navigator.clipboard
                          .writeText(discoveryText(discovery.report))
                          .then(() => {
                            notify('Compte rendu copié');
                          });
                      }}
                    >
                      <Copy aria-hidden="true" />
                      Copier pour la proposition
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant={discovery !== null ? 'ghost' : 'outline'}
                    size={discovery !== null ? 'icon-sm' : 'sm'}
                    aria-label={discovery !== null ? 'Modifier le compte rendu' : undefined}
                    onClick={() => {
                      setDiscoveryOpen(true);
                    }}
                  >
                    {discovery !== null ? <Pencil aria-hidden="true" /> : 'Rédiger le compte rendu'}
                  </Button>
                </>
              }
            />
            <CardContent>
              {discovery !== null ? (
                <dl className="m-0 grid grid-cols-1 gap-x-5 gap-y-4 min-[760px]:grid-cols-2">
                  {DISCOVERY_FIELDS.map((field) => (
                    <div key={field.key}>
                      <dt className="mb-0.5 text-label text-muted-foreground">{field.label}</dt>
                      <dd className="m-0 whitespace-pre-line text-[13px] leading-5">
                        {discovery.report[field.key] !== '' ? discovery.report[field.key] : '—'}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="m-0 text-[13px] text-muted-foreground">
                  Aucun compte rendu : contexte, douleurs, outils actuels, décideur, budget et
                  échéance du rendez-vous découverte.
                </p>
              )}
            </CardContent>
          </Card>

          <ProposalsCard detail={detail} />
          <SignatureCard detail={detail} />

          <Card>
            <CardBar start={<h2 className="m-0 text-heading">Activité</h2>} />
            <ActivityComposer companyId={company.id} dealId={deal.id} />
            <ActivityFeed
              activities={detail.activities}
              showDeal={false}
              label="Journal de l’affaire"
            />
          </Card>
        </Stack>

        <Stack className="gap-5">
          <AppointmentsCard
            companyId={company.id}
            dealId={deal.id}
            contact={detail.contacts[0] ?? detail.companyContacts.find((c) => c.primary) ?? null}
            appointments={detail.appointments}
            types={detail.appointmentTypes}
          />

          <Card>
            <CardHead
              title="Prochaine action"
              description={nextReminder?.automatic === true ? 'Créée automatiquement' : undefined}
            />
            <CardContent className="grid gap-2">
              {deal.nextAction !== null ? (
                <>
                  <span className="text-sm font-semibold">{deal.nextAction.subject}</span>
                  {nextReminder !== null ? (
                    <>
                      <span className="text-[13px] text-muted-foreground">
                        {formatReminderLong(nextReminder)}
                      </span>
                      <span className="text-[13px] text-muted-foreground">
                        {[REMINDER_TYPE_LABEL[nextReminder.type], nextReminder.contactName]
                          .filter((part): part is string => part !== null)
                          .join(', ')}
                      </span>
                      {nextReminder.note !== null ? (
                        <span className="whitespace-pre-line text-[13px]">{nextReminder.note}</span>
                      ) : null}
                    </>
                  ) : null}
                  {deal.flag === 'late' ? (
                    <Badge variant="warning" className="justify-self-start">
                      Rappel en retard
                    </Badge>
                  ) : null}
                  <div className="mt-1 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={done.isPending}
                      onClick={() => {
                        if (deal.nextAction !== null) {
                          done.mutate(deal.nextAction.reminderId);
                        }
                      }}
                    >
                      <Check aria-hidden="true" />
                      Marquer comme fait
                    </Button>
                    {nextReminder !== null ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setReminder({ kind: 'edit', reminder: nextReminder });
                        }}
                      >
                        Reporter
                      </Button>
                    ) : null}
                  </div>
                </>
              ) : (
                <>
                  {open ? (
                    <Badge variant="warning" className="justify-self-start">
                      Sans prochaine action
                    </Badge>
                  ) : (
                    <span className="text-[13px] text-muted-foreground">Affaire close.</span>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="justify-self-start"
                    onClick={() => {
                      const stage = asOpenStage(deal.stage);
                      setReminder({
                        kind: 'create',
                        companyId: company.id,
                        dealId: deal.id,
                        ...(stage !== null ? { subject: STAGE_NEXT_SUBJECT[stage] } : {}),
                      });
                    }}
                  >
                    Programmer un rappel
                  </Button>
                </>
              )}
              {otherReminders.length > 0 ? (
                <MiniList>
                  {otherReminders.map((item) => (
                    <MiniRow key={item.id}>
                      <MiniCell primary={item.subject} secondary={REMINDER_TYPE_LABEL[item.type]} />
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        className="font-mono"
                        aria-label={`Reporter : ${item.subject}`}
                        onClick={() => {
                          setReminder({ kind: 'edit', reminder: item });
                        }}
                      >
                        {formatReminderDue(item)}
                      </Button>
                    </MiniRow>
                  ))}
                </MiniList>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHead
              title="Montants"
              description="HT"
              actions={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Modifier les montants"
                  onClick={() => {
                    setEditing(true);
                  }}
                >
                  <Pencil aria-hidden="true" />
                </Button>
              }
            />
            <CardContent>
              <KvList>
                <KvRow label="Signature (mise en place)" mono>
                  {deal.setup === null ? 'À chiffrer' : eurOrDash(deal.setup)}
                </KvRow>
                <KvRow label="Hébergement" mono>
                  {deal.monthly === null ? 'À chiffrer' : `${eurOrDash(deal.monthly)} / mois`}
                </KvRow>
                <KvRow label="Frais d’IA (estimation)" mono>
                  {deal.aiMonthly === null ? '—' : `${eurOrDash(deal.aiMonthly)} / mois`}
                </KvRow>
                <KvRow label="Valeur sur 12 mois" mono>
                  {eurOrDash(deal.twelveMonthValue)}
                </KvRow>
                <KvRow label="Probabilité" mono>
                  {percentLabel(deal.probability)}
                </KvRow>
                <KvRow label="Montant pondéré" mono>
                  <span className="font-semibold">{eurOrDash(deal.weighted)}</span>
                </KvRow>
                <KvRow label="Signature prévue" mono>
                  {deal.expectedCloseDate !== null ? formatDate(deal.expectedCloseDate) : '—'}
                </KvRow>
              </KvList>
            </CardContent>
          </Card>

          <Card>
            <CardHead
              title="Contacts"
              actions={
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditing(true);
                    }}
                  >
                    Choisir
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const primary = detail.companyContacts.find((contact) => contact.primary);
                      setContactMode({
                        kind: 'create',
                        companyId: company.id,
                        companyName: company.name,
                        primaryName: primary !== undefined ? contactLine(primary) : null,
                      });
                    }}
                  >
                    Ajouter
                  </Button>
                </>
              }
            />
            <CardContent>
              {detail.contacts.length === 0 ? (
                <p className="m-0 text-[13px] text-muted-foreground">Aucun contact lié.</p>
              ) : (
                <MiniList>
                  {detail.contacts.map((contact) => (
                    <MiniRow key={contact.id}>
                      <MiniCell primary={contactLine(contact)} secondary={contact.jobTitle} />
                      <div className="flex flex-wrap justify-end gap-1">
                        {contact.primary ? <Badge variant="neutral">Principal</Badge> : null}
                        {contact.roles.map((role) => (
                          <Tag key={role}>{ROLE_LABEL[role]}</Tag>
                        ))}
                      </div>
                    </MiniRow>
                  ))}
                </MiniList>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHead
              title="Documents"
              actions={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDocumentOpen(true);
                  }}
                >
                  Ajouter
                </Button>
              }
            />
            <CardContent>
              {detail.documents.length === 0 ? (
                <EmptyState
                  className="py-4"
                  title="Aucun document"
                  description="Proposition, cahier des charges, contrat : déposer le PDF ou ajouter un lien."
                />
              ) : (
                <MiniList>
                  {detail.documents.map((document) => (
                    <MiniRow key={document.id}>
                      <MiniCell
                        primary={
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-left font-medium hover:underline"
                            onClick={() => {
                              void openDealDocument(document);
                            }}
                          >
                            {document.kind === 'file' ? (
                              <FileText className="size-3.5 text-muted-foreground" aria-hidden="true" />
                            ) : null}
                            {document.label}
                            {document.kind === 'link' ? (
                              <ExternalLink
                                className="size-3.5 text-muted-foreground"
                                aria-hidden="true"
                              />
                            ) : null}
                          </button>
                        }
                        secondary={`${DOCUMENT_CATEGORY_LABEL[document.category]} · ${formatInstantDate(document.createdAt)}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Retirer ${document.label}`}
                        onClick={() => {
                          setDeletingDoc(document);
                        }}
                      >
                        <Trash2 aria-hidden="true" />
                      </Button>
                    </MiniRow>
                  ))}
                </MiniList>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHead title="Informations" />
            <CardContent>
              <KvList>
                <KvRow label="Porteur">
                  <DealOwner detail={detail} />
                </KvRow>
                <KvRow label="Modèle">{MODEL_LABEL[deal.model]}</KvRow>
                <KvRow label="Origine">{ORIGIN_LABEL[company.origin]}</KvRow>
                <KvRow label="Entreprise">
                  <Link
                    to="/companies/$companyId"
                    params={{ companyId: company.id }}
                    className="underline underline-offset-[3px]"
                  >
                    {company.name}
                  </Link>
                </KvRow>
              </KvList>
            </CardContent>
          </Card>
        </Stack>
      </Split21>

      <LostDialog
        detail={detail}
        open={lostOpen}
        onClose={() => {
          setLostOpen(false);
        }}
      />
      <ConvertDialog
        detail={detail}
        open={convertOpen}
        onClose={() => {
          setConvertOpen(false);
        }}
      />
      <EditDealDialog
        detail={editing ? detail : null}
        onClose={() => {
          setEditing(false);
        }}
      />
      <DiscoveryDialog
        open={discoveryOpen}
        companyId={company.id}
        dealId={deal.id}
        previous={
          discovery !== null ? { id: discovery.activity.id, report: discovery.report } : null
        }
        onClose={() => {
          setDiscoveryOpen(false);
        }}
      />
      <DocumentDialog
        open={documentOpen}
        dealId={deal.id}
        onClose={() => {
          setDocumentOpen(false);
        }}
      />
      <ConfirmDialog
        open={deletingDoc !== null}
        onClose={() => {
          setDeletingDoc(null);
        }}
        onConfirm={() => {
          if (deletingDoc !== null) {
            deleteDoc.mutate(deletingDoc.id);
          }
        }}
        title="Retirer ce document ?"
        subtitle={
          deletingDoc !== null
            ? `Le lien « ${deletingDoc.label} » est retiré de l’affaire ; le fichier lui-même n’est pas touché.`
            : undefined
        }
        note={deleteDoc.isError ? errorMessage(deleteDoc.error, 'Échec du retrait.') : undefined}
        confirmLabel="Retirer"
        busy={deleteDoc.isPending}
        variant="destructive"
      />
      <ReminderDialog
        mode={reminder}
        onClose={() => {
          setReminder(null);
        }}
      />
      <ContactDialog
        mode={contactMode}
        onClose={() => {
          setContactMode(null);
        }}
        onCreated={(contact) => {
          linkContact.mutate([...detail.contacts.map((item) => item.id), contact.id]);
        }}
      />
    </>
  );
}
